import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import * as z from 'zod';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import CheckboxInput from '@/components/functional/CheckboxInput/CheckboxInput.tsx';

const loginSchema = z.object({
  email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
  password: z.string().min(1, { message: 'This field is required.' }),
});

type LoginSchema = z.infer<typeof loginSchema>;

const LoginPage: FC = () => {
  const { control, handleSubmit } = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const { t } = useTranslation();
  const {
    loginQuery: { mutateAsync: login, isPending: isLoadingLogin, error: errorLogin, isError: isErrorLogin },
  } = useAuthContext();

  const handleLogin = (data: LoginSchema) => {
    void (async () => {
      try {
        if (isLoadingLogin) return;

        await login({
          email: data.email,
          password: data.password,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <form className="w-full max-w-[428px] flex flex-col gap-4" onSubmit={handleSubmit(handleLogin)}>
        <div className="flex flex-col gap-4">
          {isErrorLogin && <Alert status="error">{errorLogin?.message}</Alert>}
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.email?.message}
                value={value}
                onChange={onChange}
                label={t('label.email')}
                placeholder={t('placeholder.email')}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.password?.message}
                value={value}
                onChange={onChange}
                type={'password'}
                label={t('label.password')}
                placeholder={t('placeholder.password')}
              />
            )}
          />
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
            <CheckboxInput label={t('label.rememberMe')} />
            <Link to={Routes.FORGOT_PASSWORD}>
              <Button size="sm" color="ghost" className="px-0 sm:px-4">
                {t('button.forgotPassword')} ?
              </Button>
            </Link>
          </div>
          <LoadingButton loading={isLoadingLogin} type="submit" color="neutral" className="w-full">
            {t('button.login')}
          </LoadingButton>
        </div>
        <div className="flex justify-center sm:justify-end">
          <Link to={Routes.REGISTER}>
            <Button color="ghost">{t('button.createNewAccount')}</Button>
          </Link>
        </div>
      </form>
    </BrandCardSideContent>
  );
};

export default LoginPage;
